import { Axial, DIRECTIONS, axialKey, axialToWorld, neighbor, packKey, roundAxial, worldToAxial } from './HexCoordinates';
import { CORNERS, SEA_LEVEL } from './HexMetrics';

/**
 * Definition of the terrain surface.
 *
 * The elevation stored on a cell describes its *centre*. Everything between
 * centres is interpolated, and the grid vertices are where that interpolation
 * is pinned: a vertex sits at the mean elevation of the three cells meeting
 * there. Two neighbouring hexes therefore share both endpoints of their common
 * edge, which makes the surface watertight without any stitching geometry.
 *
 * This lives apart from HexChunk because it is domain logic, not a meshing
 * detail — the debug overlays need it too, and so will raycasting and building
 * placement later.
 */

export interface HexCellData extends Axial {
  /** Height of the ground. On a lake cell this is the **bed**, not the water. */
  elevation: number;
  /** Biome id, or undefined for a world that has no biome layer. */
  biome?: number;
  /** Which of the six edges a river crosses, one bit per direction. */
  river?: number;
  /**
   * Where a lake's surface stands over this cell, or undefined off a lake.
   *
   * A lake sits at its own height — this is what the sea level is for the sea,
   * except that every body has its own. It has to travel separately from the
   * elevation because the ground under it is still ground: the mesh draws the
   * bed, and the mirror is laid over it.
   */
  lakeLevel?: number;
  /** Woodland richness, 0 or absent where nothing grows. */
  forest?: number;
  /** Stone richness, 0 or absent where there is none. */
  stone?: number;
  /**
   * Shoal richness, 0 or absent where there is none.
   *
   * The one deposit that lives **in the water**, so everything reading it has
   * to stop treating a flooded cell as a cell with nothing on it — the same
   * rule the fishery's site condition and its production bonus obey.
   */
  fish?: number;
  /**
   * Which edges a road crosses on this cell, or absent where none does.
   *
   * **Client-side only, like `levelled`** — and for a different reason than the
   * river beside it: a river is a layer of the world and arrives with the
   * terrain, while a road is game state that a player laid and that a world
   * swap takes away. It is written here so that everything drawing on the
   * surface reads one map of cells, rather than the ribbon keeping a second.
   */
  road?: number;
  /**
   * Droga tu jest, ale jeszcze nikogo nie niesie szybciej.
   *
   * Widać ją od razu — zapłacone jest od razu — a liczy się dopiero, gdy
   * skończy się kłaść. Osobne pole, bo „jest" i „działa" to dwa różne fakty
   * i tylko drugi wchodzi do liczenia trasy.
   */
  roadPending?: boolean;
  /**
   * The ground here has been levelled: something is built on it.
   *
   * **Client-side only.** It is not a layer of the world and the server never
   * sends it — the server knows a building stands here and that is the fact;
   * this is what the renderer does about it.
   *
   * Read where a vertex height is worked out, so everything that draws on the
   * surface — the mesh, the cursor overlay, the route line, the X — agrees
   * without being told separately.
   */
  levelled?: boolean;
  /**
   * Na to pole wychodzi budynek z sąsiedniego (pomost, łódź) i teren ma tu
   * **nie sięgać wyżej** niż ten poziom — wysokość budynku.
   *
   * Tylko po stronie klienta, jak `levelled`, i tylko do rysowania: ruch
   * i reguły miejsca czytają `elevation`, bo obniżenie jest wyglądem, a nie
   * zmianą świata.
   */
  lowered?: number;
}

/**
 * Wysokość środka pola **do rysowania** — z obniżeniem pod wystającym
 * budynkiem. Mechanika czyta `elevation`.
 */
export function groundLevel(cell: HexCellData): number {
  return cell.lowered === undefined ? cell.elevation : Math.min(cell.elevation, cell.lowered);
}

/**
 * The three cells meeting at corner `corner` of `cell`. Corner j sits between
 * edge j-1 and edge j, so it is shared with the neighbours across both.
 */
function cellsAtCorner(cell: Axial, corner: number): Axial[] {
  return [cell, neighbor(cell, (corner + 5) % 6), neighbor(cell, corner)];
}

/**
 * Corner offsets on the doubled integer lattice.
 *
 * Scaling the plane by two turns every hex centre and every corner into whole
 * numbers: a centre lands on (3q, 2r + q) and the corners sit at these fixed
 * offsets from it. Exact integers, so the three cells meeting at a vertex all
 * arrive at the identical pair with no rounding involved.
 */
const CORNER_LATTICE: ReadonlyArray<readonly [number, number]> = [
  [2, 0],
  [1, -1],
  [-1, -1],
  [-2, 0],
  [-1, 1],
  [1, 1],
];

/**
 * Stable identity for a cell's centre vertex, on the same doubled lattice as
 * the corners. Unlike a corner it belongs to one cell only, but it needs an
 * identity for the same reason: so that a vertex can be spoken about without
 * reference to the chunk that happens to be drawing it.
 */
export function centerKey(cell: Axial): number {
  return packKey(3 * cell.q, 2 * cell.r + cell.q);
}

/**
 * Stable identity for a grid vertex. Three different (cell, corner) pairs
 * describe the same physical vertex and all three produce this same number.
 */
export function cornerKey(cell: Axial, corner: number): number {
  const [dx, dz] = CORNER_LATTICE[corner];
  return packKey(3 * cell.q + dx, 2 * cell.r + cell.q + dz);
}

/** A cell is water when the sea covers it, or a lake stands on it. */
export function isWater(cell: HexCellData): boolean {
  return cell.elevation < SEA_LEVEL || cell.lakeLevel !== undefined;
}

/**
 * The height of the water on a cell: the sea for anything below the waterline,
 * a lake's own surface where there is one, and nothing on dry ground.
 */
export function waterLevel(cell: HexCellData): number | null {
  if (cell.lakeLevel !== undefined) return cell.lakeLevel;
  return cell.elevation < SEA_LEVEL ? SEA_LEVEL : null;
}

/** True when any of the three cells meeting at this vertex is water. */
export function cornerTouchesWater(
  cells: Map<number, HexCellData>,
  cell: Axial,
  corner: number
): boolean {
  for (const candidate of cellsAtCorner(cell, corner)) {
    const data = cells.get(axialKey(candidate.q, candidate.r));
    if (data && isWater(data)) return true;
  }
  return false;
}

/**
 * Elevation of a grid vertex: the mean over the cells actually present.
 * At the edge of the world fewer than three exist, and the mean is taken over
 * what is there.
 *
 * A vertex touching water is pinned to that water's surface instead of being
 * averaged. Without that, averaging a depth into a land vertex would drag the
 * shore below the surface while leaving it outside the water mesh — a strip of
 * land under the waterline with no water drawn over it. Pinning also makes the
 * water cells' own rim exactly flat, so the surface meets the shore along the
 * cell boundary rather than somewhere unpredictable up the slope.
 *
 * A lake pins to **its own level**, which is the whole reason the level travels
 * per cell: the sea has one surface and lakes have one each. Where two waters
 * meet at a vertex — a lake spilling towards the coast — the higher wins, since
 * the lower one is the one that would be seen through.
 */
export function cornerElevation(
  cells: Map<number, HexCellData>,
  cell: Axial,
  corner: number
): number {
  let sum = 0;
  let count = 0;
  let water: number | null = null;
  let platform: number | null = null;
  let cap: number | null = null;

  for (const candidate of cellsAtCorner(cell, corner)) {
    const data = cells.get(axialKey(candidate.q, candidate.r));
    if (!data) continue;

    if (data.lowered !== undefined) {
      cap = cap === null ? data.lowered : Math.min(cap, data.lowered);
    }

    // Two kinds of cell do not average: water sits on its own mirror, and
    // levelled ground sits at its own height. Each **claims** the vertex rather
    // than contributing to a mean, and among claims of the same kind the
    // highest wins — the rule two lakes meeting at a corner have always
    // followed, because the lower one would be seen through the higher.
    const surface = waterLevel(data);
    if (surface !== null) {
      water = water === null ? surface : Math.max(water, surface);
      continue;
    }

    if (data.levelled) {
      platform = platform === null ? data.elevation : Math.max(platform, data.elevation);
      continue;
    }

    sum += groundLevel(data);
    count += 1;
  }

  // Water beats a platform, whichever is higher, and this is not a preference:
  // measured on real terrain, letting the platform win pulled a shoreline
  // vertex up by as much as **eleven levels**, because a site may sit on a
  // clifftop with the sea beside it — the flatness rule for a site skips water
  // on purpose. The result was a spike of ground standing out of the sea at one
  // corner. A building by the water therefore has a pad that is flat except
  // where it meets the water, which is exactly as flat as it was before.
  if (water !== null) return water;
  if (platform !== null) return platform;

  // Pod wystającym budynkiem teren schodzi w dół, nigdy w górę. Platforma
  // wygrywa z tym (wyżej) — obniżenie nie kopie pod cudzym budynkiem ani drogą.
  const mean = count > 0 ? sum / count : 0;
  return cap === null ? mean : Math.min(mean, cap);
}

/**
 * Wysokość powierzchni terenu w dowolnym punkcie, w poziomach — albo `null`,
 * gdy pola pod tym punktem jeszcze nie ma.
 *
 * **Ten sam kształt, który rysuje chunk**: wachlarz sześciu trójkątów od
 * środka pola (na jego wysokości) do narożników (`cornerElevation`, z wodą
 * i wyrównanym gruntem, które zgłaszają roszczenie). Punkt trafia w jeden
 * trójkąt i dostaje wysokość z interpolacji jego trzech wierzchołków — więc
 * coś, co idzie po terenie, idzie dokładnie po tym, co widać.
 */
export function surfaceAt(world: Map<number, HexCellData>, x: number, z: number): number | null {
  const fractional = worldToAxial(x, z);
  const cell = roundAxial(fractional.q, fractional.r);
  const data = world.get(axialKey(cell.q, cell.r));
  if (!data) return null;

  const center = axialToWorld(cell.q, cell.r);
  const dx = x - center.x;
  const dz = z - center.z;

  // Narożnik `i` leży pod kątem −i·60°, więc sektor liczy się od kąta ujemnego.
  const turn = ((-Math.atan2(dz, dx) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  const sector = Math.min(5, Math.floor(turn / (Math.PI / 3)));
  const a = CORNERS[sector];
  const b = CORNERS[sector + 1];

  // p = u·a + v·b; środek dostaje resztę wagi.
  const det = a.x * b.z - a.z * b.x;
  const u = Math.abs(det) < 1e-9 ? 0 : (dx * b.z - dz * b.x) / det;
  const v = Math.abs(det) < 1e-9 ? 0 : (a.x * dz - a.z * dx) / det;
  const w = 1 - u - v;

  return (
    w * groundLevel(data) +
    u * cornerElevation(world, cell, sector) +
    v * cornerElevation(world, cell, (sector + 1) % 6)
  );
}

/**
 * Steepness at a vertex, in elevation levels per hex step.
 *
 * The surface is a fan of triangles from each cell centre out to its six
 * corners, so every edge of it spans exactly one hex radius. The steepest of
 * the edges meeting at a vertex is therefore its gradient, and because they all
 * have the same length the level difference alone describes it — no world units
 * involved, so the number does not move if HEX_SIZE ever does.
 *
 * Both ends of an edge measure the same difference, which is what keeps a
 * shared corner at one value whichever of its three cells asks for it: the
 * corner compares itself against the same three centres either way.
 */
export function centerSlope(
  cells: Map<number, HexCellData>,
  cell: HexCellData
): number {
  let steepest = 0;

  for (let corner = 0; corner < 6; corner++) {
    const drop = Math.abs(cell.elevation - cornerElevation(cells, cell, corner));
    if (drop > steepest) steepest = drop;
  }

  return steepest;
}

/**
 * The same measure seen from a grid vertex: the steepest of the edges running
 * out to the centres of the cells that meet there.
 *
 * Water cells are left out of it. A vertex touching water is pinned to sea
 * level while the seabed underneath sits at its depth, so counting that pair
 * would report a cliff at every single shoreline — it measures how water cells
 * are built, not how the ground behaves. What is left is the landward answer:
 * how steeply the shore climbs out of the sea, which is the thing that decides
 * whether it is a beach or a cliff.
 *
 * `elevation` is the corner's own height, which every caller has already had to
 * compute to place the vertex.
 */
export function cornerSlope(
  cells: Map<number, HexCellData>,
  cell: Axial,
  corner: number,
  elevation: number
): number {
  let steepest = 0;

  for (const candidate of cellsAtCorner(cell, corner)) {
    const data = cells.get(axialKey(candidate.q, candidate.r));
    if (!data || isWater(data)) continue;

    const drop = Math.abs(elevation - data.elevation);
    if (drop > steepest) steepest = drop;
  }

  return steepest;
}

/**
 * How much of the sky a cell can see, as a shade between 0 and 1.
 *
 * Ambient occlusion, worked out from the **elevation map** rather than from the
 * screen. A valley floor with ground rising on every side is dark; a ridge is
 * not. It costs eighteen lookups per cell, is computed once when a chunk is
 * built, and — this is the point of it — **does not depend on where the sun
 * is**. That matters as soon as the world has a night: when the key light drops
 * away, occlusion is the only thing still describing the shape of the ground.
 *
 * The measure is the steepest horizon in each of the six directions, out to
 * three cells, taken as levels of rise per cell of distance. Screen-space AO
 * would have been the other option and is the wrong one here: the game renders
 * into a buffer of about a thousand by six hundred and scales it up whole, so a
 * screen-space effect flickers along every diagonal it touches, and this one
 * is stable because it is a fact about the terrain.
 */
const AO_REACH = 3;

/** Rise per cell at which a direction counts as fully blocked. */
const AO_STEEP = 3.5;

/** How dark the deepest hollow gets. Nothing is ever pitch black. */
const AO_STRENGTH = 0.45;

export function cellOcclusion(world: Map<number, HexCellData>, cell: HexCellData): number {
  let blocked = 0;

  for (let direction = 0; direction < 6; direction++) {
    let steepest = 0;

    for (let step = 1; step <= AO_REACH; step++) {
      const there = world.get(
        axialKey(
          cell.q + DIRECTIONS[direction].q * step,
          cell.r + DIRECTIONS[direction].r * step
        )
      );
      // Off the loaded map: no horizon known, and guessing one would draw a
      // dark rim around whatever has streamed in so far.
      if (!there) continue;

      steepest = Math.max(steepest, (there.elevation - cell.elevation) / step);
    }

    blocked += Math.min(1, Math.max(0, steepest / AO_STEEP));
  }

  return 1 - AO_STRENGTH * (blocked / 6);
}

/**
 * Occlusion at a corner: the mean of the three cells that meet there.
 *
 * The same averaging the elevation uses, and for the same reason — three fans
 * share the vertex and all three have to arrive at one value, or the shading
 * breaks along the cell boundary.
 */
export function cornerOcclusion(
  world: Map<number, HexCellData>,
  cell: HexCellData,
  corner: number
): number {
  let sum = 0;
  let count = 0;

  for (const at of cellsAtCorner(cell, corner)) {
    const there = world.get(axialKey(at.q, at.r));
    if (!there) continue;
    sum += cellOcclusion(world, there);
    count++;
  }

  return count === 0 ? 1 : sum / count;
}
